import React, { useState, useMemo } from 'react';
import { Form, Dropdown, Image } from 'react-bootstrap';
import { Search } from 'lucide-react';

export interface DropdownOption {
  value: string;
  label: string;
  thumbnailImageUrl?: string | undefined;
  level?: number | undefined;
  isRoot?: boolean | undefined;
  id?: string | number | undefined;
}

interface FilterDropdownProps {
  label: string;
  value: string;
  options: DropdownOption[];
  placeholder?: string;
  onSelect: (value: string) => void;
  showDividers?: boolean;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  value,
  options,
  placeholder = 'All',
  onSelect,
  showDividers = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption?.label || placeholder;

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    const lowerSearch = searchText.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(lowerSearch));
  }, [options, searchText]);

  return (
    <>
      <Form.Label className="mb-1" style={{ fontSize: '0.75rem', fontWeight: '500', color: '#ccc' }}>
        {label}
      </Form.Label>
      <Dropdown
        onSelect={(eventKey) => onSelect(eventKey || '')}
        onToggle={(isOpen) => {
          if (!isOpen) setSearchText('');
        }}
      >
        <Dropdown.Toggle variant="dark" size="sm" style={{ fontSize: '0.8rem', width: '100%', textAlign: 'left' }}>
          {displayText}
        </Dropdown.Toggle>
        <Dropdown.Menu
          style={{
            maxHeight: '400px',
            overflowY: 'auto',
            width: '100%',
            backgroundColor: '#fff',
          }}
        >
          <div style={{ padding: '0.5rem', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
            <div style={{ position: 'relative' }}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => {
                  e.stopPropagation();
                  setSearchText(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: '0.8rem', paddingLeft: '2rem' }}
              />
              <Search
                size={14}
                style={{
                  position: 'absolute',
                  left: '0.5rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                }}
              />
            </div>
          </div>
          <Dropdown.Divider style={{ margin: 0 }} />
          <Dropdown.Item eventKey="" style={{ fontSize: '0.8rem', color: '#000' }}>
            {placeholder}
          </Dropdown.Item>
          {filteredOptions.length > 0 && <Dropdown.Divider />}
          {filteredOptions.length === 0 && searchText && (
            <div style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', color: '#6c757d', textAlign: 'center' }}>
              No results found
            </div>
          )}
          {filteredOptions.map((option, index) => (
            <React.Fragment key={option.id || option.value}>
              {showDividers && option.isRoot && index > 0 && <Dropdown.Divider />}
              <Dropdown.Item
                eventKey={option.value}
                style={{
                  fontSize: '0.8rem',
                  color: '#000',
                  padding: '0.5rem 1rem',
                  paddingLeft: option.level !== undefined ? `${1 + option.level * 1.5}rem` : '1rem',
                  fontWeight: option.isRoot ? '600' : '400',
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  {option.thumbnailImageUrl ? (
                    <Image
                      src={option.thumbnailImageUrl}
                      alt={option.label}
                      width={24}
                      height={24}
                      className="rounded"
                      style={{ objectFit: 'cover', flexShrink: 0 }}
                    />
                  ) : (
                    <div
                      className="rounded"
                      style={{
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#e0e0e0',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span>{option.label}</span>
                </div>
              </Dropdown.Item>
            </React.Fragment>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

